import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import type {
  BasicEstimationsPerProjectsResponse,
  EstimationBasicResponse,
} from '@/api/schema/data-contracts';
import { ApiEstimationStage } from '@/api/schema/data-contracts';
import IconCaretDown from '@/assets/Icons/IconCaretDown';
import { Button } from '@/components/atoms/Button';
import Dropdown from '@/components/atoms/Dropdown';
import { Loader } from '@/components/atoms/Loader';
import { useMessages } from '@/hooks/messages';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import { sortExpressions } from '@/utils/sorting';

import EstimationVersionModal from './EstimationVersionModal';
import { estimationTabMessageDefinitions } from './messages';

interface EstimationVersionDropdownProps {
  projectId: string;
  estimationId: string;
}

interface Versions {
  current: EstimationBasicResponse;
  latest: EstimationBasicResponse;
}

const EstimationVersionDropdown: FC<EstimationVersionDropdownProps> = ({
  projectId,
  estimationId,
}) => {
  const messages = useMessages(estimationTabMessageDefinitions);

  const { useGetEstimations } = useEstimationQueryActions();
  const { data } = useGetEstimations({ projectId });

  const hasCanCreateEstimationVersionPermission = useHasEstimationPermission(
    estimationId,
    'CAN_CREATE_NEW_VERSION',
  );

  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

  const versions = useMemo<Versions | undefined>(() => {
    if (!data) {
      return undefined;
    }
    const sorted = data.items.sort(sortExpressions.byEstimationVersionNumberDesc);
    const current = data.items.find(
      (x: BasicEstimationsPerProjectsResponse) => x.estimation.id === estimationId,
    )?.estimation;
    const latest = sorted[0]?.estimation;
    // Either can be absent - an empty list, or an estimation the project listing does not carry
    // (permissions, a version just deleted). Hide the dropdown instead of taking the page down.
    if (!current || !latest) {
      return undefined;
    }
    return { current, latest };
  }, [data, estimationId]);

  const canCreateNewVersion = useMemo(
    () =>
      versions &&
      versions.current === versions.latest &&
      versions.current.stage === ApiEstimationStage.APPROVED &&
      hasCanCreateEstimationVersionPermission,
    [versions, hasCanCreateEstimationVersionPermission],
  );

  if (!versions) {
    return <Loader noMargin size='s' />;
  }

  return (
    <div className='dropdown' data-testid='version-dropdown'>
      <Dropdown
        dataTestId='version-dropdown-button'
        btnClassName='btn btn-outline-secondary border-none btn-sm dropdown-toggle'
        button={
          <>
            {messages.currentVersion({
              versionNumber: versions.current.versionNumber.toString(),
            })}
            <span>
              <IconCaretDown className='ml-2 inline-block ltr:ml-1 rtl:mr-1' />
            </span>
          </>
        }
      >
        <ul className='!min-w-[170px] !p-5 whitespace-nowrap'>
          <li className='text-sm'>
            <Link
              to='/estimation/detail/$estimationId'
              params={{ estimationId: versions.current.id }}
            >
              {messages.activeVersion({
                versionNumber: versions.current.versionNumber.toString(),
              })}
            </Link>
          </li>
          <li className='text-sm'>
            <Link
              to='/estimation/detail/$estimationId'
              params={{ estimationId: versions.latest.id }}
            >
              {messages.workingVersion({
                versionNumber: versions.latest.versionNumber.toString(),
              })}
            </Link>
          </li>
          <hr className='mt-5 mb-5' />
          <li>
            <Button
              data-testid='create-new-version-button'
              variant='primary'
              outlined
              onClick={() => setIsVersionModalOpen(true)}
              disabled={!canCreateNewVersion}
            >
              {messages.createNewVersion()}
            </Button>
          </li>
          <li className='text-sm'>
            <Link
              data-testid='view-all-versions-link'
              to='/estimation-versions/$projectId'
              params={{ projectId }}
              className='place-content-center underline'
            >
              {messages.viewAllVersions()}
            </Link>
          </li>
        </ul>
      </Dropdown>
      <EstimationVersionModal
        open={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        estimationId={estimationId}
        allowEstimationCreation={canCreateNewVersion}
      />
    </div>
  );
};

export default EstimationVersionDropdown;
